import { merge } from 'lodash-es';
import React, { type ComponentProps, useId } from 'react';

import type { VariantProp } from '../../common/colors';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WrapTypes } from '../../utilities/shared/Wrap';
import { sizing } from '../../common/sizing';
import { VariantProvider } from '../../common/variant';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, fontWeights, shadows, styled } from '../../stitches.config';
import { Body, BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { Small, SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { TitleSansSizes } from '../../text/Title';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { wrapCSS } from '../../utilities/shared/Wrap';
import { LabelTooltip } from '../Tooltip/Tooltip';

const StatDisplay = styled('span', {
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  color: '$$displayColor',

  variants: {
    alignment: {
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
      start: {
        justifyContent: 'flex-start',
      },
    },
    size: {
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...LargeSansSizes,
      },
      'extra-large': {
        ...TitleSansSizes,
        fontWeight: fontWeights.bold,
      },
    },
    variant: {
      alternative: {
        color: colors.headingAlternativeLight,

        [darkThemeSelector]: {
          color: colors.headingAlternativeDark,
        },
      },
      attention: {
        color: colors.headingAttentionLight,

        [darkThemeSelector]: {
          color: colors.headingAttentionDark,
        },
      },
      brand: {
        color: colors.headingBrandLight,

        [darkThemeSelector]: {
          color: colors.headingBrandDark,
        },
      },
      negative: {
        color: colors.headingNegativeLight,

        [darkThemeSelector]: {
          color: colors.headingNegativeDark,
        },
      },
      neutral: {
        color: colors.headingNeutralLight,

        [darkThemeSelector]: {
          color: colors.headingNeutralDark,
        },
      },
      positive: {
        color: colors.headingPositiveLight,

        [darkThemeSelector]: {
          color: colors.headingPositiveDark,
        },
      },
    },
  },
});

const StatFootnoteLabel = styled(Small, {
  fontWeight: fontWeights.bold,
});

const StatFootnoteValue = styled(Small, {
  fontWeight: fontWeights.bold,
});

const StatFootnote = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
});

const StatLabel = styled(Text, {
  color: '$$labelColor',
  fontWeight: fontWeights.bold,
  whiteSpace: 'nowrap',
});

const StatLegend = styled(Body, {
  display: 'flex',
  flexDirection: 'row',
  color: '$$legendColor',
});

const StatHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: '$8',
});

const StatContainer = styled('div', {
  display: 'flex',

  $$legendColor: colors.bodyNeutralLight,
  $$displayColor: colors.headingNeutralLight,

  [darkThemeSelector]: {
    $$legendColor: colors.bodyNeutralDark,
    $$displayColor: colors.headingNeutralDark,
  },

  variants: {
    alignment: {
      center: {
        alignItems: 'center',
      },
      end: {
        alignItems: 'flex-end',
      },
      start: {
        alignItems: 'flex-start',
      },
    },
    arrangement: {
      'leading-display': {
        flexDirection: 'column-reverse',
      },
      'leading-label': {
        flexDirection: 'column',
      },
    },
    proportions: {
      equal: {},
      auto: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',
        [selectors.focus]: {
          outline: 'none',
          borderRadius: '$8',
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {},
    },
    selected: {
      true: {
        $$labelColor: colors.bodyBrandLight,
        $$legendColor: colors.bodyBrandLight,
        $$displayColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          $$labelColor: colors.bodyBrandDark,
          $$legendColor: colors.bodyBrandDark,
          $$displayColor: colors.headingBrandDark,
        },
      },
      false: {},
    },
    size: {
      small: {
        gap: '$2',
      },
      medium: {
        gap: '$4',
      },
      large: {
        gap: '$4',
      },
      'extra-large': {
        gap: '$4',
      },
    },
    ticker: {
      true: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '$8',
      },
      false: {
        justifyContent: 'flex-end',
      },
    },
  },

  compoundVariants: [
    {
      proportions: 'equal',
      ticker: false,
      css: {
        flex: '1 1 0',
      },
    },
    {
      arrangement: 'leading-display',
      ticker: false,
      css: {
        flexDirection: 'column-reverse',
      },
    },
    {
      arrangement: 'leading-label',
      ticker: false,
      css: {
        flexDirection: 'column',
      },
    },
  ],
});

type StatAlignmentProp = 'center' | 'end' | 'start';
type StatArrangementProp = 'leading-display' | 'leading-label';
type StatProportionsProp = 'equal' | 'auto';
type StatSizeProp = 'small' | 'medium' | 'large' | 'extra-large';

export type StatProps = {
  /**
   * The alignment of the stat.
   */
  alignment?: StatAlignmentProp;
  /**
   * The arrangement of the stat.
   */
  arrangement?: StatArrangementProp;
  /**
   * A footnote to display below the stat.
   */
  footnote?: {
    label: React.ReactNode;
    value: React.ReactNode;
    variant?: VariantProp;
  };
  /**
   * The label to display above the stat. Can be a string, a React node, or an object with children and variant props.
   */
  label: React.ReactNode | { copy: React.ReactNode; variant?: VariantProp };
  /**
   * The legend to display to the right of the label.
   */
  legend?: React.ReactNode;
  /**
   * The proportions of the stat.
   */
  proportions?: StatProportionsProp;
  /**
   * Whether the stat is selected.
   */
  selected?: boolean;
  /**
   * The size of the stat.
   */
  size?: StatSizeProp;
  /**
   * Whether the stat is a ticker.
   */
  ticker?: boolean;
  /**
   * A tooltip to display when hovering over the stat.
   */
  tooltip?: React.ReactNode;
  /**
   * The value to display in the stat.
   */
  value: React.ReactNode;
  /**
   * A variant to apply to the stat.
   */
  variant?: VariantProp;
  /**
   * A callback to call when the stat is clicked.
   */
  onClick?: () => void;
} & ComponentProps<typeof StatContainer>;

function Stat({
  alignment,
  arrangement,
  footnote,
  label,
  legend,
  onClick,
  selected,
  size,
  ticker,
  tooltip,
  value,
  variant,
  ...remaining
}: StatProps) {
  const labelVariant =
    label && typeof label === 'object' && 'variant' in label ? label.variant : variant;
  const isPressable = !!onClick;

  return (
    <StatContainer
      alignment={alignment}
      arrangement={arrangement}
      selected={selected}
      size={size}
      ticker={ticker}
      tabIndex={isPressable ? 0 : undefined}
      isPressable={isPressable}
      onClick={onClick}
      role={isPressable ? 'button' : undefined}
      onKeyDown={(e) => {
        if (isPressable && e.code === 'Enter') {
          onClick();
        }
      }}
      {...remaining}
    >
      <StatHeader>
        {label && (
          <LabelTooltip
            contents={tooltip}
            preset={size === 'large' ? 'body' : 'small'}
            variant={labelVariant}
          >
            <StatLabel>
              {typeof label === 'object' && 'copy' in label ? label.copy : label}
            </StatLabel>
          </LabelTooltip>
        )}
        {legend && <StatLegend>{legend}</StatLegend>}
      </StatHeader>
      <StatDisplay alignment={alignment} size={size} variant={variant}>
        <VariantProvider value={variant}>{value}</VariantProvider>
      </StatDisplay>
      {footnote && (
        <StatFootnote>
          <StatFootnoteLabel>{footnote.label}</StatFootnoteLabel>
          <StatFootnoteValue context="body" variant={footnote.variant}>
            {footnote.value}
          </StatFootnoteValue>
        </StatFootnote>
      )}
    </StatContainer>
  );
}

const StatsSeparator = styled('div', {
  display: 'flex',
  width: '$2',
  height: '$16',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});

const StatsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
  width: '100%',
  padding: sizing.squish,

  variants: {
    gutter: {
      all: {},
      vertical: {},
      horizontal: {},
      top: {},
      right: {},
      bottom: {},
      left: {},
      none: {},
    },
    proportions: {
      auto: {},
      equal: {},
    },
    size: {
      small: {
        columnGap: '$4',
        rowGap: '$12',
      },
      medium: {
        columnGap: '$8',
        rowGap: '$16',
      },
      large: {
        columnGap: '$12',
        rowGap: '$20',
      },
      'extra-large': {
        columnGap: '$20',
        rowGap: '$32',
      },
    },
    ticker: {
      true: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '$16',
        overflow: 'auto',
      },
      false: {},
    },
  },
});

export type StatsProps = {
  alignment?: StatAlignmentProp;
  arrangement?: StatArrangementProp;
  gutter?: GutterProp;
  proportions?: StatProportionsProp;
  selected?: boolean;
  spacing?: SpacingScale;
  size?: StatSizeProp;
  stats: StatProps[];
  ticker?: boolean;
  wrap?: WrapTypes;
} & ComponentProps<typeof StatsContainer>;

export function Stats({
  alignment = 'start',
  gutter = 'all',
  arrangement = 'leading-label',
  proportions = 'equal',
  selected,
  size = 'large',
  stats,
  ticker = false,
  wrap = 'wrap',
  ...remaining
}: StatsProps) {
  const id = useId();
  return (
    <StatsContainer
      proportions={proportions}
      size={size}
      ticker={ticker}
      css={merge(
        gutterCSS({ context: size === 'small' ? 'content' : 'application', gutter }),
        wrapCSS(wrap),
      )}
      {...remaining}
    >
      {stats.map((stat) => (
        <React.Fragment key={`${id}-${stat.label}`}>
          <Stat
            alignment={alignment}
            arrangement={arrangement}
            proportions={proportions}
            selected={selected}
            size={size}
            ticker={ticker}
            {...stat}
          />
          {ticker && stats.indexOf(stat) !== stats.length - 1 && <StatsSeparator />}
        </React.Fragment>
      ))}
    </StatsContainer>
  );
}
